
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDownWideNarrow,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  FileSearch2,
  Globe2,
  Rss,
  X,
} from "lucide-react";
import FilterSidebar from "./FilterSidebar.jsx";
import NewsCard from "./NewsCard.jsx";
import { DATE_OPTIONS, EMPTY_FILTERS, applyFilters, countActive } from "../data/News.js";
import { LuLayers3 } from "react-icons/lu";

const PAGE_SIZE = 6;

function Breadcrumb() {
  return (
    <nav
      aria-label="Breadcrumb"
      className="breadcrumb-in absolute left-4 top-4 z-20 sm:left-8 sm:top-6"
    >
      <ol className="flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#141419] shadow-lg shadow-black/5 backdrop-blur-md sm:text-sm">
        <li>
          <a
            href="/"
            className="inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#DA2128]"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 10v10h14V10" />
              <path d="M9 20v-6h6v6" />
            </svg>
            Home
          </a>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li>
          <a  className="transition-colors duration-300 hover:text-[#DA2128]">
            Newsroom
          </a>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm">Press Releases</li>
      </ol>
    </nav>
  );
}

/* -------------------------------- hero ------------------------------- */
function PageHero({ eyebrow, titleA, titleB, description, stats }) {
  return (
    <section className="relative overflow-hidden bg-blush">
      {/* decorations */}
      <div className="absolute inset-0 dot-grid opacity-70" />
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-brand/15 to-ember/10 blur-3xl" />
      <div className="absolute -bottom-32 right-[-60px] w-[460px] h-[460px] rounded-full bg-gradient-to-tr from-deeprose/15 to-ember/10 blur-3xl" />
      <div className="absolute top-10 right-[12%] w-24 h-24 rounded-full border-[10px] border-ember/20 animate-float-slower hidden lg:block" />
      <div
        className="absolute bottom-12 right-[30%] w-14 h-14 rounded-2xl bg-brand/10 rotate-12 animate-float-slow hidden lg:block"
        style={{ "--fl-rot": "12deg" }}
      />
      {/* <p className="absolute -bottom-8 left-0 font-display font-black text-[130px] leading-none text-stroke-red opacity-40 select-none hidden xl:block whitespace-nowrap">
        ASSOCHAM
      </p> */}

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 pb-14 sm:pt-16 sm:pb-16">
        
        <Breadcrumb />

        <div className="mt-7 max-w-3xl m-auto">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-brand/20 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-deeprose"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ember" />
            {eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-5 font-display font-bold text-[32px] leading-[1.02] sm:text-[34px] text-ink tracking-tight text-center m-auto"
          >
            {titleA}{" "}
            <em className="italic text-transparent bg-clip-text bg-gradient-to-r from-crimson via-brand to-ember">
              {titleB}
            </em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.24 }}
            className="mt-5 text-[15px] leading-relaxed text-ink/60 max-w-2xl font-medium"
          >
            {description}
          </motion.p>

          {/* stats */}
          {/* <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.32 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            {stats.map((s, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 rounded-2xl bg-white/85 backdrop-blur ring-1 ring-brand/15 px-4 py-2.5 shadow-[0_10px_30px_-18px_rgba(180,35,24,0.4)]"
              >
                <s.icon className="w-4 h-4 text-brand" />
                <span className="text-[12.5px] font-extrabold text-ink">{s.value}</span>
                <span className="text-[11px] font-semibold text-mute">{s.label}</span>
              </div>
            ))}
          </motion.div> */}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- pagination ---------------------------- */
function Pagination({ page, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="mt-10 flex items-center justify-center gap-2">
      <button
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className="w-10 h-10 rounded-xl grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-brand hover:ring-brand disabled:opacity-35 disabled:pointer-events-none transition-all"
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`relative w-10 h-10 rounded-xl text-[13px] font-extrabold transition-all ${
            n === page
              ? "text-white"
              : "ring-1 ring-ink/15 text-ink/70 hover:text-brand hover:ring-brand"
          }`}
        >
          {n === page && (
            <motion.span
              layoutId="page-pill"
              className="absolute inset-0 rounded-xl bg-gradient-to-br from-crimson to-deeprose shadow-lg shadow-brand/30"
              transition={{ type: "spring", stiffness: 420, damping: 32 }}
            />
          )}
          <span className="relative">{n}</span>
        </button>
      ))}
      <button
        disabled={page === total}
        onClick={() => onChange(page + 1)}
        className="w-10 h-10 rounded-xl grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-brand hover:ring-brand disabled:opacity-35 disabled:pointer-events-none transition-all"
        aria-label="Next page"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* --------------------------- chips + listing -------------------------- */
export default function NewsroomListing({
  eyebrow,
  titleA,
  titleB,
  description,
  items,
  listTitle,
  showSource = false,
}) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("newest");
  const resultsRef = useRef(null);

  const filtered = useMemo(() => {
    const out = applyFilters(items, filters);
    out.sort((a, b) =>
      sort === "newest" ? b.dateISO.localeCompare(a.dateISO) : a.dateISO.localeCompare(b.dateISO)
    );
    return out;
  }, [items, filters, sort]);

  useEffect(() => setPage(1), [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clamped = Math.min(page, totalPages);
  const start = (clamped - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);

  const latest = items.reduce((a, b) => (a.dateISO > b.dateISO ? a : b));
  const subjectCount = new Set(items.flatMap((i) => i.subjects)).size;
  const active = countActive(filters);

  const scrollToResults = () =>
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const changePage = (n) => {
    setPage(n);
    scrollToResults();
  };

  // active chips model
  const chips = [
    filters.keyword.trim() && {
      key: "kw",
      label: `"${filters.keyword.trim()}"`,
      clear: () => setFilters((f) => ({ ...f, keyword: "" })),
    },
    ...filters.types.map((t) => ({
      key: `t-${t}`,
      label: t,
      clear: () => setFilters((f) => ({ ...f, types: f.types.filter((v) => v !== t) })),
    })),
    ...filters.subjects.map((s) => ({
      key: `s-${s}`,
      label: s,
      clear: () => setFilters((f) => ({ ...f, subjects: f.subjects.filter((v) => v !== s) })),
    })),
    filters.date && {
      key: "d",
      label: DATE_OPTIONS.find((o) => o.value === filters.date)?.label ?? filters.date,
      clear: () => setFilters((f) => ({ ...f, date: "" })),
    },
    ...filters.regions.map((r) => ({
      key: `r-${r}`,
      label: r,
      clear: () => setFilters((f) => ({ ...f, regions: f.regions.filter((v) => v !== r) })),
    })),
  ].filter(Boolean);

  return (
    <main>
      <PageHero
        eyebrow={eyebrow}
        titleA={titleA}
        titleB={titleB}
        description={description}
        stats={[
          { icon: LuLayers3, value: items.length, label: "total stories" },
          {
            icon: CalendarDays,
            value: new Date(latest.dateISO).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }),
            label: "latest update",
          },
          { icon: Globe2, value: subjectCount, label: "subjects covered" },
        ]}
      />

      <section className="relative bg-cream py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 sm:gap-10">
            {/* filters — standard horizontal bar on top */}
            <div className="rounded-2xl bg-white ring-1 ring-brand/10 shadow-[0_10px_40px_-24px_rgba(180,35,24,0.35)] px-4 sm:px-5 py-4">
              <FilterSidebar
                filters={filters}
                setFilters={setFilters}
                pool={items}
                onClear={() => setFilters(EMPTY_FILTERS)}
                onSearch={scrollToResults}
              />
            </div>

            {/* results */}
            <div ref={resultsRef} className="scroll-mt-32 min-w-0">
              {/* header */}
              <div className="flex flex-wrap items-center justify-between gap-4 pb-5 border-b-2 border-ink">
                <div>
                  <h2 className="font-display font-bold text-[28px] sm:text-[32px] text-ink tracking-tight flex items-center gap-3">
                    {listTitle}
                    {/* <span className="text-brand">({filtered.length})</span> */}
                    <button
                      className="text-brand hover:text-deeprose transition-colors"
                      title="Subscribe via RSS"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Rss className="w-5 h-5" />
                    </button>
                  </h2>
                  {/* <p className="mt-1 text-[13px] font-semibold text-mute">
                    {filtered.length === 0
                      ? "No matching results"
                      : `Showing results ${start + 1} to ${Math.min(start + PAGE_SIZE, filtered.length)}`}
                  </p> */}
                </div>

                <div className="flex items-center gap-2">
                  <ArrowDownWideNarrow className="w-4 h-4 text-mute" />
                  <div className="flex rounded-xl ring-1 ring-ink/15 overflow-hidden text-[12px] font-extrabold">
                    {[
                      ["newest", "Newest first"],
                      ["oldest", "Oldest first"],
                    ].map(([v, l]) => (
                      <button
                        key={v}
                        onClick={() => setSort(v)}
                        className={`px-3.5 py-2 transition-colors ${
                          sort === v
                            ? "bg-gradient-to-r from-crimson to-deeprose text-white"
                            : "bg-white text-ink/60 hover:text-brand"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* active chips */}
              <AnimatePresence>
                {chips.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-wrap items-center gap-2 py-4">
                      <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-mute mr-1">
                        Active filters —
                      </span>
                      {chips.map((c) => (
                        <motion.span
                          key={c.key}
                          layout
                          initial={{ opacity: 0, scale: 0.85 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.85 }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-brand/25 pl-3 pr-1.5 py-1 text-[11.5px] font-bold text-deeprose shadow-sm"
                        >
                          {c.label}
                          <button
                            onClick={c.clear}
                            className="w-[18px] h-[18px] rounded-full grid place-items-center bg-blush text-brand hover:bg-brand hover:text-white transition-colors"
                          >
                            <X className="w-2.5 h-2.5" strokeWidth={3} />
                          </button>
                        </motion.span>
                      ))}
                      <button
                        onClick={() => setFilters(EMPTY_FILTERS)}
                        className="text-[11.5px] font-extrabold text-brand underline underline-offset-4 decoration-ember hover:text-deeprose transition-colors ml-1"
                      >
                        Clear all
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* list */}
              {pageItems.length > 0 ? (
                <AnimatePresence mode="popLayout">
                  {pageItems.map((item, i) => (
                    <NewsCard key={item.id} item={item} index={i} showSource={showSource} />
                  ))}
                </AnimatePresence>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-20 flex flex-col items-center text-center"
                >
                  <div className="w-20 h-20 rounded-3xl bg-blush ring-1 ring-brand/15 grid place-items-center">
                    <FileSearch2 className="w-9 h-9 text-brand" />
                  </div>
                  <h3 className="mt-6 font-display font-bold text-2xl text-ink">
                    No results found
                  </h3>
                  <p className="mt-2 text-[13.5px] text-mute max-w-sm">
                    Nothing matches your current filter combination. Try removing a subject,
                    widening the date range, or clearing all filters.
                  </p>
                  <button
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="mt-6 rounded-xl bg-gradient-to-r from-crimson to-deeprose text-white text-[13px] font-extrabold px-6 py-3 shadow-lg shadow-brand/30 hover:shadow-brand/50 transition-all"
                  >
                    Clear all filters
                  </button>
                </motion.div>
              )}

              <Pagination page={clamped} total={totalPages} onChange={changePage} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}